import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Switch } from "@headlessui/react";
import { TbWorld } from "react-icons/tb";
import { FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import golivecam from "../../assets/images/icons/golive-cam.png";
import shareIcon from "../../assets/images/icons/shareIcon.svg";
import profileimg from "../../assets/profile.png";
import useLiveBroadcast from "../../hook/useLiveBroadcast";
import {
  authHeaders,
  formatPrice,
  LiveCommentsPanel,
  InfoTip,
  LiveProductCard,
  LiveSummary,
  LiveTimeLimitWarning,
  PendingLiveBanner,
  useLiveComments,
  useLiveProducts,
  useNow,
} from "./LiveShoppingParts";
import {
  goLiveBlockReason,
  PlanLimitsBar,
  PRIVATE_LIVE_UPGRADE_REASON,
  UpgradePlanModal,
  useSubscriptionStatus,
} from "../subscription/PlanPrompts";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const MAX_LIVE_PRODUCTS = 3;
const COUNTDOWN_PATTERN = /^(\d{1,2}):([0-5]\d)$/;

// Digits typed into the countdown become HH:MM, e.g. 0005 -> 00:05, 0115 -> 01:15
const formatCountdownInput = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
};

const Seller = () => {
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [title, setTitle] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [chatEnabled, setChatEnabled] = useState(true);
  const [starting, setStarting] = useState(false);
  const [sellerProducts, setSellerProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [offers, setOffers] = useState({}); // productId -> { offer_percentage, countdown, quantity_limit }
  const [busyProductId, setBusyProductId] = useState(null);
  const [featuringId, setFeaturingId] = useState(null);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const { status: planStatus, refresh: refreshPlanStatus } = useSubscriptionStatus();

  const videoRef = useRef(null);
  const {
    cameraStream,
    cameraError,
    startCamera,
    live,
    viewerCount,
    socket,
    goLive,
    stopLive,
    pendingLive,
    resumeLive,
    discardLive,
    endedLive,
    sessionLimit,
  } = useLiveBroadcast("marketplace");

  const streamId = live?.streamId;
  const now = useNow();
  const { comments, sendComment } = useLiveComments(streamId, socket);
  const { products: liveProducts, refreshProducts } = useLiveProducts(streamId, socket);

  useEffect(() => {
    axios
      .post(`${API_BASE_URL}/seller/get-seller-profile`, {}, { headers: authHeaders() })
      .then((res) => setSeller(res.data.result))
      .catch((error) => toast.error(error.response?.data?.message || "Unable to load your seller profile"));
  }, []);

  useEffect(() => {
    if (videoRef.current && cameraStream) videoRef.current.srcObject = cameraStream;
  }, [cameraStream]);

  useEffect(() => {
    if (!seller?.id) return;
    const timer = setTimeout(() => {
      axios
        .post(
          `${API_BASE_URL}/product/get-products`,
          { filters: { seller_id: Number(seller.id), ...(search.trim() && { search: search.trim() }) }, page: 1, limit: 100 },
          { headers: authHeaders() }
        )
        .then((res) => setSellerProducts(res.data.result?.products || []))
        .catch(() => setSellerProducts([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [seller?.id, search]);

  const handleGoLive = async () => {
    if (!title.trim()) {
      toast.error("Please add a title before going live");
      return;
    }
    if (!cameraStream) {
      toast.error("Allow camera access to go live");
      return;
    }
    const blockReason = goLiveBlockReason(planStatus, { privateAudience: visibility === "private" });
    if (blockReason) {
      setUpgradeReason(blockReason);
      return;
    }

    setStarting(true);
    try {
      await goLive({
        init: { stream_type: "marketplace" },
        // "Private" is the followers audience, not the API's premium-only visibility
        configuration: {
          visibility: "public",
          view_audience: visibility === "private" ? "followers" : "public",
          chat_enabled: chatEnabled,
          reactions_enabled: true,
        },
        metadata: { title: title.trim(), category: "Marketplace", tags: ["marketplace"] },
      });
    } catch (error) {
      console.error("Go Live error:", error);
      toast.error(error.message || "Unable to start the live");
    } finally {
      setStarting(false);
    }
  };

  // Nothing is saved before going live, so cancelling only leaves the page
  const handleCancel = () => {
    if (title.trim() && !window.confirm("Discard this live setup?")) return;
    navigate("/sellprofile", { replace: true });
  };

  const handleResumeLive = async () => {
    setStarting(true);
    try {
      const resumed = await resumeLive();
      setTitle(resumed.title || "");
      setVisibility(resumed.view_audience === "public" ? "public" : "private");
      setChatEnabled(resumed.chat_enabled);
    } catch (error) {
      toast.error(error.message || "Unable to resume the live");
    } finally {
      setStarting(false);
    }
  };

  const handleDiscardLive = async () => {
    setStarting(true);
    try {
      await discardLive();
      toast.success("Your live has ended");
    } catch (error) {
      toast.error(error.message || "Unable to end the live");
    } finally {
      setStarting(false);
    }
  };

  const handleStopLive = async () => {
    setStarting(true);
    try {
      await stopLive();
      toast.success("Your live has ended");
    } catch (error) {
      toast.error(error.message || "Unable to end the live");
    } finally {
      setStarting(false);
    }
  };

  // Stop, End Live and the server ending the live (auto-end, admin) all land here
  useEffect(() => {
    if (!endedLive) return;
    setOffers({});
    refreshPlanStatus();
    if (endedLive.byServer) toast.info("Your live has ended");
  }, [endedLive, refreshPlanStatus]);

  const handleToggleComments = async (enabled) => {
    if (!streamId) {
      setChatEnabled(enabled);
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/live/${streamId}/toggle-comments`, { chat_enabled: enabled }, { headers: authHeaders() });
      setChatEnabled(enabled);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update comments");
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/live-shopping/${streamId}`);
      toast.success("Live link copied");
    } catch (error) {
      toast.error("Unable to copy the live link");
    }
  };

  const updateOffer = (productId, field, value) =>
    setOffers((prev) => ({ ...prev, [productId]: { ...prev[productId], [field]: value } }));

  const handleStartSelling = async (product) => {
    if (planStatus && !planStatus.product_selling_enabled) {
      setUpgradeReason("Selling products during a live is not included in your plan.");
      return;
    }
    const offer = offers[product.id] || {};
    const offerPercentage = Number(offer.offer_percentage) || 0;
    const quantityLimit = offer.quantity_limit === undefined || offer.quantity_limit === "" ? null : Number(offer.quantity_limit);
    const countdown = (offer.countdown || "").trim();
    const countdownMatch = countdown.match(COUNTDOWN_PATTERN);

    if (offerPercentage < 0 || offerPercentage >= 100) {
      toast.error("Discount must be between 0 and 99%");
      return;
    }
    if (quantityLimit !== null && (!Number.isInteger(quantityLimit) || quantityLimit < 1 || quantityLimit > product.product_qty)) {
      toast.error(`Quantity must be between 1 and ${product.product_qty}`);
      return;
    }
    if (countdown && !countdownMatch) {
      toast.error("Countdown must be in HH:MM format");
      return;
    }
    const durationMinutes = countdownMatch ? Number(countdownMatch[1]) * 60 + Number(countdownMatch[2]) : null;
    if (countdownMatch && (durationMinutes < 1 || durationMinutes > 1440)) {
      toast.error("Countdown must be between 00:01 and 24:00");
      return;
    }

    setBusyProductId(product.id);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/live/${streamId}/products`,
        {
          sellerId: String(seller.id),
          products: [
            {
              product_id: String(product.id),
              offer_percentage: offerPercentage || null,
              quantity_limit: quantityLimit,
              duration_minutes: durationMinutes,
            },
          ],
        },
        { headers: authHeaders() }
      );
      if (res.data.status !== "success") throw new Error(res.data.message);
      await refreshProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Unable to start selling");
    } finally {
      setBusyProductId(null);
    }
  };

  const handleStopSelling = async (productId) => {
    try {
      await axios.delete(`${API_BASE_URL}/live/${streamId}/products/${productId}`, { headers: authHeaders() });
      await refreshProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to stop selling");
    }
  };

  const handleFeature = async (productId) => {
    setFeaturingId(productId);
    try {
      await axios.patch(
        `${API_BASE_URL}/live/${streamId}/products/feature`,
        { productId: String(productId) },
        { headers: authHeaders() }
      );
      await refreshProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to feature the product");
    } finally {
      setFeaturingId(null);
    }
  };

  const attachedProductIds = new Set(liveProducts.map((p) => String(p.productId)));
  const sellerName = seller?.store_name || seller?.name;
  const sellerBadge = (
    <div className="absolute left-0 top-0 z-10 flex items-center gap-2 rounded-br-2xl bg-white/60 px-4 py-2 backdrop-blur-sm">
      <img
        src={seller?.profile_image?.url || profileimg}
        alt=""
        className="h-9 w-9 rounded-full bg-white object-cover"
      />
      <div className="text-black">
        <p className="text-sm font-semibold">{sellerName}</p>
        <p className="text-[10px]">
          {(live?.startedAt || new Date()).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
        </p>
      </div>
    </div>
  );

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6">
        <LeftResponsivesidebar />
        <Headmenu />

        {live && <LiveTimeLimitWarning sessionLimit={sessionLimit} now={now} />}
        {pendingLive && !live && (
          <PendingLiveBanner
            title={pendingLive.title}
            busy={starting}
            canResume={!!cameraStream}
            onResume={handleResumeLive}
            onEnd={handleDiscardLive}
          />
        )}

        {live ? (
          <h1 className="mt-6 text-lg font-semibold">GoLive</h1>
        ) : (
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            placeholder="Add Title..."
            className="mt-6 w-full max-w-xl rounded-full border border-[#1DE78B] px-4 py-2 text-sm focus:outline-none"
          />
        )}
        {!live && (
          <div className="mt-3 max-w-xl">
            <PlanLimitsBar status={planStatus} />
          </div>
        )}

        <div className="mt-4 flex flex-col gap-4 lg:flex-row">
          <div className="min-w-0 flex-1 rounded-xl bg-[#EBF8ED]">
            <div className="relative flex h-[260px] items-center justify-center overflow-hidden rounded-xl bg-black text-white sm:h-[400px]">
              {sellerBadge}
              {cameraStream ? (
                // Mirrored like a front camera for the seller only; viewers receive the unmirrored broadcast
                <video ref={videoRef} autoPlay playsInline muted className="h-full w-full -scale-x-100 object-cover" />
              ) : (
                <div className="flex flex-col items-center px-6 text-center">
                  <img src={golivecam} alt="" className="mb-3" />
                  <p className="text-lg font-semibold">{cameraError ? "Allow Access To Camera" : "Starting camera..."}</p>
                  {cameraError && (
                    <>
                      <p className="mt-1 max-w-md text-xs text-gray-300">
                        Your browser is not allowing Live Producer access to your camera. Go to your browser settings and
                        allow camera permission.
                      </p>
                      <button
                        onClick={startCamera}
                        className="mt-5 rounded-lg bg-[#D9D9D9] px-8 py-2 font-semibold text-black"
                      >
                        Retry
                      </button>
                    </>
                  )}
                </div>
              )}
              {live && (
                <>
                  <button
                    onClick={handleShare}
                    title="Copy live link"
                    className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2"
                  >
                    <img src={shareIcon} alt="Share" className="h-4 w-4" />
                  </button>
                  <span className="absolute bottom-4 left-4 text-sm font-semibold">{viewerCount} Viewers</span>
                  <span className="absolute bottom-4 right-4 rounded-full bg-[#BE1F2E] px-4 py-1 text-xs font-semibold">
                    LIVE
                  </span>
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={chatEnabled}
                  onChange={handleToggleComments}
                  className={`${chatEnabled ? "bg-[#095228]" : "bg-gray-400"} relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${chatEnabled ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                  />
                </Switch>
                <span className="text-sm">Comments</span>
              </div>

              {live ? (
                <button
                  onClick={handleStopLive}
                  disabled={starting}
                  className="rounded-full bg-[#BE1F2E] px-12 py-2 font-semibold text-white disabled:opacity-60"
                >
                  Stop
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCancel}
                    disabled={starting}
                    className="rounded-full border border-gray-400 bg-white px-6 py-2 font-semibold text-gray-700 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <div className="flex items-center gap-1">
                    <TbWorld className="text-lg" />
                    <select
                      value={visibility}
                      onChange={(e) =>
                        e.target.value === "private" && planStatus && !planStatus.private_live_enabled
                          ? setUpgradeReason(PRIVATE_LIVE_UPGRADE_REASON)
                          : setVisibility(e.target.value)
                      }
                      className="bg-transparent text-sm focus:outline-none"
                    >
                      <option value="public">Public</option>
                      <option value="private">Private</option>
                    </select>
                    <InfoTip
                      label="About live visibility"
                      text={visibility === "private" ? "Only your followers can watch this live." : "Everyone can watch this live."}
                    />
                  </div>
                  <button
                    onClick={handleGoLive}
                    disabled={starting || !seller}
                    className="rounded-full bg-gradient-to-r from-[#08AEEA] to-[#1DE78B] px-12 py-2 font-semibold text-white disabled:opacity-60"
                  >
                    {starting ? "Starting..." : "Go Live"}
                  </button>
                </div>
              )}
            </div>

            {endedLive && !live && (
              <div className="px-4 pb-4">
                <LiveSummary
                  streamId={endedLive.streamId}
                  streamType="marketplace"
                  startedAt={endedLive.startedAt}
                  endedAt={endedLive.endedAt}
                />
              </div>
            )}

            {live && (
              <div className="space-y-6 px-4 pb-6">
                {liveProducts.length > 0 && (
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {liveProducts.map((product) => (
                      <LiveProductCard key={product.liveStreamProductId} product={product} now={now}>
                        <p className="text-xs">
                          Qty : <span className="font-semibold text-[#BE1F2E]">{product.soldQuantity ?? 0} Sold</span> / Out of{" "}
                          {product.quantityLimit ?? (product.soldQuantity ?? 0) + (product.remainingQuantity ?? 0)}
                        </p>
                        <div className="flex flex-wrap justify-center gap-2 pt-1">
                          {!product.isFeatured && (
                            <button
                              onClick={() => handleFeature(product.productId)}
                              disabled={featuringId !== null}
                              title="Show this product first to viewers"
                              className="rounded-lg border border-[#095228] px-3 py-1 text-xs text-[#095228] disabled:opacity-50"
                            >
                              {featuringId === product.productId ? "Featuring..." : "Feature"}
                            </button>
                          )}
                          <button
                            onClick={() => handleStopSelling(product.productId)}
                            className="rounded-lg bg-[#7A1018] px-3 py-1 text-xs text-white"
                          >
                            Stop Selling
                          </button>
                        </div>
                      </LiveProductCard>
                    ))}
                  </div>
                )}

                <section className="rounded-lg bg-white shadow">
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 md:px-6">
                    <h2 className="font-semibold">Your Product</h2>
                    <div className="relative w-full max-w-[220px]">
                      <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search Products"
                        className="w-full rounded-full border border-gray-300 bg-gray-100 py-2 pl-9 pr-3 text-sm focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-100 text-left">
                          <th className="px-3 py-2">PRODUCTS</th>
                          <th className="px-2 py-2">PRICE</th>
                          <th className="px-2 py-2">DISCOUNT</th>
                          <th className="px-2 py-2">DISCOUNT PRICE</th>
                          <th className="px-2 py-2">COUNTDOWN TIMER</th>
                          <th className="px-2 py-2">QTY</th>
                          <th className="px-2 py-2 text-center">START SELLING</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerProducts.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-6 text-center text-gray-500">
                              No products found
                            </td>
                          </tr>
                        )}
                        {sellerProducts.map((product) => {
                          const offer = offers[product.id] || {};
                          const percentage = Number(offer.offer_percentage) || 0;
                          const isSelling = attachedProductIds.has(String(product.id));
                          const canSell =
                            !isSelling &&
                            product.inStock &&
                            product.product_qty > 0 &&
                            liveProducts.length < MAX_LIVE_PRODUCTS &&
                            busyProductId === null;
                          return (
                            <tr key={product.id} className="border-t">
                              <td className="px-3 py-2">
                                <div className="flex min-w-[180px] items-center gap-2">
                                  <img
                                    src={product.product_media?.[0]?.url || "/image/default.png"}
                                    alt=""
                                    className="h-12 w-12 rounded object-contain"
                                  />
                                  <div>
                                    <p className="line-clamp-2 font-semibold uppercase">{product.product_name}</p>
                                    <p className="text-[10px] text-green-700">Available Qty {product.product_qty}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-2 py-2">{formatPrice(product.discount_price || product.product_price)}</td>
                              <td className="px-2 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  max="99"
                                  value={offer.offer_percentage ?? ""}
                                  onChange={(e) => updateOffer(product.id, "offer_percentage", e.target.value)}
                                  disabled={isSelling}
                                  placeholder="%"
                                  className="w-16 rounded-md border px-2 py-2"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  readOnly
                                  value={
                                    percentage > 0 && percentage < 100
                                      ? formatPrice(((product.discount_price || product.product_price) * (100 - percentage)) / 100)
                                      : ""
                                  }
                                  className="w-20 rounded-md border bg-gray-50 px-2 py-2"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  value={offer.countdown ?? ""}
                                  onChange={(e) => updateOffer(product.id, "countdown", formatCountdownInput(e.target.value))}
                                  disabled={isSelling}
                                  inputMode="numeric"
                                  maxLength={5}
                                  placeholder="HH:MM"
                                  className="w-20 rounded-md border px-2 py-2"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="number"
                                  min="1"
                                  max={product.product_qty}
                                  value={offer.quantity_limit ?? ""}
                                  onChange={(e) => updateOffer(product.id, "quantity_limit", e.target.value)}
                                  disabled={isSelling}
                                  placeholder={String(product.product_qty)}
                                  className="w-16 rounded-md border px-2 py-2"
                                />
                              </td>
                              <td className="px-2 py-2 text-center">
                                <button
                                  onClick={() => handleStartSelling(product)}
                                  disabled={!canSell}
                                  title={isSelling ? "Already selling" : liveProducts.length >= MAX_LIVE_PRODUCTS ? `Up to ${MAX_LIVE_PRODUCTS} products per live` : "Start selling"}
                                  className="h-8 w-10 rounded-lg bg-[#095228] text-lg font-bold text-white disabled:opacity-40"
                                >
                                  {isSelling ? "✓" : "+"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}
          </div>

          {live && (
            <LiveCommentsPanel
              comments={comments}
              chatEnabled={chatEnabled}
              onSend={sendComment}
              className="h-[500px] w-full rounded-xl bg-[#F9FAFB] p-4 lg:h-[650px] lg:w-[300px]"
            />
          )}
        </div>
      </main>
      <UpgradePlanModal reason={upgradeReason} onClose={() => setUpgradeReason(null)} />
    </div>
  );
};

export default Seller;
